import {
  SupportedCountry,
  SupportedLanguage,
} from '~shared-components/utils/enums'

import { Flag, FlagValues } from './useFlags'

/**
 * Values frozen from LaunchDarkly (project `apac-connect`) ahead of the Unleash
 * migration. Every value below is the one production serves.
 *
 * Each flag has its own named constant and call sites import that constant
 * directly, so none of these is read through a hook any more. Keeping one
 * constant per flag — rather than pointing call sites at the shared maps — means
 * a single flag can change without moving the others.
 *
 * Most values were identical in all five LaunchDarkly environments (local,
 * develop, acceptance, preproduction, production). Where an environment differed,
 * it is called out on the constant concerned.
 *
 * `maintenancePage` is deliberately absent. It is the operational kill switch and
 * the only flag still evaluated at runtime; it lives in `useFlags.ts`.
 */

/* -------------------------------------------------------------------------- */
/* Shared shapes                                                              */
/*                                                                            */
/* Not exported on purpose: read a flag through its own constant below.        */
/* -------------------------------------------------------------------------- */

/**
 * Shared by every per-country flag that was launched in all environments.
 *
 * `DE` is `false` on purpose. Eight of the nine per-country flags key on `BD`,
 * which is not a `SupportedCountry`, and carry no `DE` key at all, so Germany
 * resolves `undefined` and reads as off today. `Record<SupportedCountry, boolean>`
 * requires the key, and freezing to live values means writing that `false` out
 * explicitly rather than switching those features on for Germany.
 *
 * `globalSearchPerCountry` is the one exception and it does not match this
 * constant — see `GLOBAL_SEARCH_PER_COUNTRY` below.
 */
const LAUNCHED_COUNTRIES: Record<SupportedCountry, boolean> = {
  AU: true,
  DE: false,
  ID: true,
  IN: true,
  KR: true,
  MY: true,
  NZ: true,
  PH: true,
  SG: true,
  TH: true,
  TW: true,
  VN: true,
}

/**
 * Built but never launched: on in local and develop only, off in acceptance,
 * preproduction and production. Frozen to the production value, so the features
 * these gate stay unreachable — which is what every environment except local and
 * develop already does.
 *
 * The code behind them is left intact pending a decision to launch or delete it.
 */
const UNLAUNCHED_COUNTRIES: Record<SupportedCountry, boolean> = {
  AU: false,
  DE: false,
  ID: false,
  IN: false,
  KR: false,
  MY: false,
  NZ: false,
  PH: false,
  SG: false,
  TH: false,
  TW: false,
  VN: false,
}

/* -------------------------------------------------------------------------- */
/* Per-flag values                                                            */
/* -------------------------------------------------------------------------- */

/**
 * On in every environment for everyone, so these features are already
 * unconditional in practice.
 *
 * They are kept as constants rather than deleted outright so that the branches
 * they guard stay visible until a product decision retires each feature.
 * Removing those branches is deliberately not part of this change.
 */
export const ADDITIONAL_ITEMS = true
export const EXPORT_INVOICES_AND_ORDERS = true
export const REQUEST_ORDER_CANCELLATION = true
export const REQUEST_ORDER_CHANGES = true
export const UPLOAD_USER_DOCUMENTS = true

/**
 * Off in every deployed environment; on in local only. The individual user
 * targets on this flag in preproduction and production are dormant: LaunchDarkly
 * serves the off variation to everyone while a flag is off, so nobody reaches the
 * pay-online flow today.
 *
 * The Adyen payment flow is left intact pending a product decision to either
 * launch or delete it.
 */
export const PAY_ONLINE = false

/**
 * Launched in all 11 APAC countries in every environment.
 */
export const EDIT_USER_PROFILE_PER_COUNTRY = LAUNCHED_COUNTRIES
export const INVOICES_PAGE_FOR_CSR_UI_PER_COUNTRY = LAUNCHED_COUNTRIES
export const INVOICES_PAGE_PER_COUNTRY = LAUNCHED_COUNTRIES
export const ORDERS_PAGE_FOR_CSR_UI_PER_COUNTRY = LAUNCHED_COUNTRIES
export const ORDER_HISTORY_LOG_PER_COUNTRY = LAUNCHED_COUNTRIES
export const PRODUCTS_PAGE_FOR_CSR_UI_PER_COUNTRY = LAUNCHED_COUNTRIES

/**
 * Launched everywhere, and the only per-country flag whose LaunchDarkly map
 * carries a `DE` key.
 *
 * LaunchDarkly serves `DE: true` here in develop, acceptance, preproduction and
 * production — the "All enabled" variation, which also covers BE, NL, PL and US.
 * This constant keeps `DE: false`, so global search and the search-style filter
 * bars are off for a session whose country resolves to `DE`, where LaunchDarkly
 * had them on.
 *
 * That divergence is unresolved. `DE` is a `SupportedCountry` and `DE01` a
 * `SupportedSalesOrganisation`, which suggests the enums are shared with the
 * European Connect app rather than APAC serving German customers — but that is an
 * inference, not a verified fact, and it cannot be settled from the frontend.
 * Confirm no APAC session resolves to `DE`, then either set this to `true` to
 * match LaunchDarkly exactly or record the loss as accepted.
 */
export const GLOBAL_SEARCH_PER_COUNTRY: Record<SupportedCountry, boolean> = {
  ...LAUNCHED_COUNTRIES,
  DE: false,
}

/**
 * Built but never launched outside local and develop.
 */
export const EXTERNAL_USER_ADMIN_PER_COUNTRY = UNLAUNCHED_COUNTRIES
export const QUOTES_PAGE_PER_COUNTRY = UNLAUNCHED_COUNTRIES

/**
 * Identical in all five environments. Values are counts, not booleans: `1`
 * enables a language and `0` disables it, and `UserEdit` compares against `1`,
 * so the shape is kept verbatim.
 *
 * Note there is no `de` key, so German is not selectable — that matches
 * LaunchDarkly today. The type is `Partial` for that reason.
 */
export const ENABLED_LANGUAGES: Partial<Record<SupportedLanguage, number>> = {
  en: 1,
  id: 0,
  ko: 1,
  th: 1,
  vi: 1,
  'zh-CN': 0,
  'zh-TW': 1,
}

/* -------------------------------------------------------------------------- */
/* Lookup by flag name                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The frozen values keyed by `Flag`, for the two consumers that resolve a flag by
 * name rather than importing one constant: `FlagRender`, whose `flag` prop names
 * the flag, and `useSyncRoutes`, which walks `routesToFlagsMap`.
 *
 * Every other consumer imports the constant it needs directly, so no component
 * reads a frozen value through a hook. `maintenancePage` is absent because it is
 * not frozen.
 *
 * The cast is unavoidable rather than lazy: `FlagValues` carries a
 * `[key: string]: boolean` index signature, which no literal holding the country
 * and language records can satisfy, and it types `enabledLanguages` as a full
 * `Record` where the frozen value is `Partial`. Asserting to `FlagValues` keeps
 * the indexed-access types the two consumers already rely on unchanged.
 */
export const FROZEN_FLAG_VALUES = ({
  [Flag.AdditionalItems]: ADDITIONAL_ITEMS,
  [Flag.EditUserProfilePerCountry]: EDIT_USER_PROFILE_PER_COUNTRY,
  [Flag.EnabledLanguages]: ENABLED_LANGUAGES,
  [Flag.ExportInvoicesAndOrders]: EXPORT_INVOICES_AND_ORDERS,
  [Flag.ExternalUserAdminPerCountry]: EXTERNAL_USER_ADMIN_PER_COUNTRY,
  [Flag.GlobalSearchPerCountry]: GLOBAL_SEARCH_PER_COUNTRY,
  [Flag.InvoicesPageForCsrUiPerCountry]: INVOICES_PAGE_FOR_CSR_UI_PER_COUNTRY,
  [Flag.InvoicesPagePerCountry]: INVOICES_PAGE_PER_COUNTRY,
  [Flag.OrderHistoryLogPerCountry]: ORDER_HISTORY_LOG_PER_COUNTRY,
  [Flag.OrdersPageForCsrUiPerCountry]: ORDERS_PAGE_FOR_CSR_UI_PER_COUNTRY,
  [Flag.PayOnline]: PAY_ONLINE,
  [Flag.ProductsPageForCsrUiPerCountry]: PRODUCTS_PAGE_FOR_CSR_UI_PER_COUNTRY,
  [Flag.QuotesPagePerCountry]: QUOTES_PAGE_PER_COUNTRY,
  [Flag.RequestOrderCancellation]: REQUEST_ORDER_CANCELLATION,
  [Flag.RequestOrderChanges]: REQUEST_ORDER_CHANGES,
  [Flag.UploadUserDocuments]: UPLOAD_USER_DOCUMENTS,
} as unknown) as FlagValues
